import { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import clsx from "clsx"
import Markdown from "react-markdown"
import remarkBreaks from "remark-breaks"
import remarkGfm from "remark-gfm"

import { Subheading } from "src/components/heading"
import { Text } from "src/components/text"
import { Avatar } from "src/components/avatar"
import { Icon } from "src/components/icon"
import { Button } from "src/components/button"
import { BorderlessTextarea } from "src/components/textarea"
import { BadgeButton } from "src/components/badge"

import { apiClient } from "src/utils"

const thinkingSteps = [
  "Thinking...",
  "Searching SEC filings...",
  "Drafting response...",
  "Conducting secondary search...",
  "Reviewing additional context...",
  "Updating draft response...",
  "Seeing if anything was missed...",
  "Considering any follow-up searches...",
  "Checking response for accuracy...",
  "Finalizing response...",
]

export default function ChatMessages() {
  const { uuid } = useParams()

  const user = useSelector((state) => state.auth.user)

  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesStreaming, setMessagesStreaming] = useState(false)
  const [messagesError, setMessagesError] = useState(false)
  const [message, setMessage] = useState("")

  const [thinkingStepIndex, setThinkingStepIndex] = useState(0)

  const messagesContainerRef = useRef(null)
  const lastElementRef = useRef(null)
  const textareaRef = useRef(null)
  const abortControllerRef = useRef(null)

  const getChatMessages = () => {
    setMessagesLoading(true)
    setMessagesError(false)
    apiClient
      .get(`/chat/messages`, { params: { chat_uuid: uuid } })
      .then((messages) => {
        setMessages(messages)
      })
      .catch(() => {
        setMessagesError(true)
      })
      .finally(() => {
        setMessagesLoading(false)
      })
  }

  const scrollToBottom = () => {
    const container = messagesContainerRef.current
    const lastElement = lastElementRef.current
    if (container && lastElement) {
      const timer = setTimeout(() => {
        const elementTop = lastElement.offsetTop
        const offset = 100 // Add some breathing room at the top
        container.scrollTo({ top: elementTop - offset, behavior: "smooth" })
      }, 200)
      return () => clearTimeout(timer)
    }
  }

  const streamResponse = async (messageInput = null) => {
    const messageToSend = messageInput || message
    if (messageToSend.trim() === "") return

    setMessage("")
    setMessages((ms) => [...ms, { role: "user", message: messageToSend }, { role: "assistant", thinking: true }])

    setMessagesLoading(true)
    setMessagesStreaming(true)
    setMessagesError(false)

    abortControllerRef.current = new AbortController()

    try {
      const response = await fetch(`${import.meta.env.VITE_STREAMING_URL}/api/chat/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          chat_uuid: uuid,
          message: messageToSend,
        }),
        signal: abortControllerRef.current.signal,
      })

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      scrollToBottom()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        setMessages((ms) => {
          const lastMessage = ms[ms.length - 1]
          if (lastMessage.role === "assistant" && lastMessage.thinking) {
            return [...ms.slice(0, -1), { role: "assistant", message: chunk }]
          }
          return [...ms.slice(0, -1), { role: "assistant", message: lastMessage.message + chunk }]
        })
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessagesError(true)
        setMessage(messageToSend)
        setMessages((ms) => [...ms.slice(0, -2)])
      }
    } finally {
      setMessagesLoading(false)
      setMessagesStreaming(false)
      setTimeout(() => textareaRef.current?.focus(), 0)
    }
  }

  const cancelStream = () => {
    abortControllerRef.current?.abort()
    setMessagesLoading(false)
    setMessagesStreaming(false)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  useEffect(() => {
    if (uuid) {
      getChatMessages()
    }
  }, [uuid])

  useEffect(() => {
    if (
      !messagesLoading &&
      !messagesError &&
      !messagesStreaming &&
      messages.length === 1 &&
      messages[0].role === "user"
    ) {
      streamResponse(messages[0].message)
    }
  }, [uuid, messages, messagesLoading, messagesError, messagesStreaming])

  useEffect(() => {
    if (
      messages.length > 0 &&
      messages[messages.length - 1].role === "assistant" &&
      messages[messages.length - 1].thinking
    ) {
      setTimeout(() => {
        if (thinkingStepIndex < thinkingSteps.length - 1) {
          setThinkingStepIndex((index) => index + 1)
        }
      }, 6000)
    } else {
      setThinkingStepIndex(0)
    }
  }, [messages, thinkingStepIndex])

  return (
    <div className="flex-1 min-h-0 flex flex-col w-full group/chatWindow">
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-x-hidden overflow-y-auto min-h-0 flex flex-col gap-y-8 w-full pb-4 pt-2 pl-4 sm:pl-2 pr-4"
      >
        {messages.slice(1, messages.length).map((message, index) => {
          const isLastElement = index === messages.length - 1 && !messagesError
          if (message.role === "assistant") {
            return (
              <div
                ref={isLastElement ? lastElementRef : null}
                key={index}
                className="flex flex-row items-start gap-4 text-pretty min-w-0"
              >
                <img src={"/logo.png"} className="w-6 h-6 shrink-0 rounded-full" />
                <div className="prose dark:prose-invert flex flex-col min-w-0">
                  <Markdown
                    remarkPlugins={[remarkGfm, remarkBreaks]}
                    components={{
                      h1: ({ children }) => (
                        <Text className="first:mt-0 mt-6 mb-2 !text-xl font-semibold flex flex-col gap-1 items-start justify-start text-zinc-950 dark:text-white">
                          <span className="!text-sm/6 capitalize !text-emerald-600">Section</span>
                          {children}
                        </Text>
                      ),
                      h2: ({ children }) => <Subheading className="mt-8 underline">{children}</Subheading>,
                      h3: ({ children }) => {
                        let bodyContent = ""
                        try {
                          bodyContent =
                            children?.length > 100
                              ? children.replace("###", "").slice(0, 100) + "..."
                              : children?.replace("###", "")
                        } catch (e) {
                          bodyContent = children
                        }
                        return <Subheading className="">{bodyContent}</Subheading>
                      },
                      h4: ({ children }) => <Subheading className="mt-8 underline">{children}</Subheading>,
                      h5: ({ children }) => <Subheading className="mt-8 underline">{children}</Subheading>,
                      h6: ({ children }) => <Subheading className="mt-8 underline">{children}</Subheading>,
                      p: ({ children }) => (
                        <Text
                          className={clsx(
                            "text-sm/6 !text-zinc-900 dark:!text-zinc-100 mb-3",
                            message.thinking && "animate-pulse !text-xs italic mt-1"
                          )}
                        >
                          {children}
                        </Text>
                      ),
                      ul: ({ children }) => (
                        <ul className="text-sm/6 list-disc list-outside ml-4 text-zinc-900 dark:text-zinc-100 my-2">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="text-sm/6 list-decimal list-outside ml-5 text-zinc-900 dark:text-zinc-100 my-2">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-sm/6 list-item text-zinc-900 dark:text-zinc-100 first:mt-0 mt-1 ml-3">
                          {children}
                        </li>
                      ),
                      a: ({ children, href }) => (
                        <a href={href} className="text-blue-500">
                          {children}
                        </a>
                      ),
                      table: ({ children }) => (
                        <div className="my-3 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
                          <table className="w-full text-sm text-left border-collapse">{children}</table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 font-medium">
                          {children}
                        </thead>
                      ),
                      tbody: ({ children }) => (
                        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">{children}</tbody>
                      ),
                      tr: ({ children }) => <tr className="even:bg-zinc-50 dark:even:bg-zinc-800/50">{children}</tr>,
                      th: ({ children }) => (
                        <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100">{children}</td>
                      ),
                    }}
                  >
                    {message.thinking ? thinkingSteps[thinkingStepIndex] : message.message}
                  </Markdown>
                  <div className="flex flex-row items-center gap-2 flex-wrap">
                    {message?.grounding_metadata?.map((metadata) => {
                      const titlePieces = metadata.title.split("_")
                      const formName = titlePieces[1]
                      const filingDate = titlePieces[2]
                      const filingDateFormatted = new Date(filingDate).toLocaleDateString("en-US", {
                        year: "2-digit",
                        month: "short",
                      })
                      const title = `${formName} ${filingDateFormatted}`
                      const formLink = metadata.uri.replace("gs://", "https://storage.googleapis.com/")
                      return (
                        <BadgeButton
                          key={metadata.uri}
                          href={formLink}
                          target="_blank"
                          className="flex flex-row items-center gap-2"
                        >
                          <Icon icon="Link" className="w-3 h-3 text-blue-500" />
                          {title}
                        </BadgeButton>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          }
          return (
            <div
              ref={isLastElement ? lastElementRef : null}
              key={index}
              className="flex flex-row items-start gap-4 text-pretty min-w-0"
            >
              <Avatar
                src={user?.picture_url}
                initials={user?.first_name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase()}
                className="w-6 h-6 shrink-0"
              />
              <Text className="min-w-0 break-words">{message.message}</Text>
            </div>
          )
        })}
        {!messagesLoading && messagesError && (
          <div ref={lastElementRef} className="flex flex-row items-start gap-4">
            <Icon icon="ExclamationTriangle" className="w-6 h-6 text-red-500" />
            <Text>Error loading messages, try again.</Text>
          </div>
        )}
      </div>
      <div className="shrink-0 mt-2 py-2 pr-1 pl-3 sm:py-3 sm:pl-5 sm:pr-3 flex flex-row items-center sm:flex-col gap-4 w-full sm:items-start justify-start sm:bg-white sm:dark:bg-black sm:border sm:border-zinc-200 sm:dark:border-zinc-800 sm:rounded-xl border-t border-zinc-200 dark:border-zinc-800">
        <BorderlessTextarea
          ref={textareaRef}
          className="min-h-12 w-full !text-base sm:!text-sm"
          placeholder="Reply..."
          value={message}
          autoFocus={true}
          onChange={(e) => setMessage(e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && message.trim() !== "" && !messagesLoading) {
              e.preventDefault()
              streamResponse()
              textareaRef.current?.focus()
            }
          }}
        />
        <Button
          className="ml-auto"
          color="emerald"
          onClick={() => {
            if (messagesStreaming) {
              cancelStream()
              return
            }

            streamResponse()
          }}
          disabled={!messagesStreaming && message.trim() === ""}
        >
          <Icon icon={messagesStreaming ? "Stop" : "ArrowUp"} className="w-4 h-4 !text-white" />
        </Button>
      </div>
    </div>
  )
}
